import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../api/client";
import { fmtDayTime } from "../home/time";
import { errorMessage } from "./api";
import QuizGuard from "./QuizGuard";
import { GlobalContext } from "../../../context";

const clock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/**
 * Quiz (prototype assessScreen): intro → timed runner → score + review.
 * API: POST /assessments/{uuid}/start (409 = resume, with questions), POST /attempts/{uuid}/submit,
 * GET /attempts/{uuid}/result.
 */
export default function QuizView({ quiz, onDone }) {
  const [phase, setPhase] = useState("intro"); // intro | run | result
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [q, setQ] = useState(0);
  const [left, setLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const { authState: { data } } = useContext(GlobalContext);
  const me = data?.user;
  const mark = [[me?.firstname, me?.lastname].filter(Boolean).join(" "), me?.email].filter(Boolean).join(" · ");
  const attemptsLeft = quiz.max_attempts ? Math.max(0, quiz.max_attempts - quiz.attempts_used) : null;
  const canStart = quiz.window === "open" && attemptsLeft !== 0;

  const begin = (data) => {
    setAttempt(data);
    setLeft(Math.max(0, Math.round(data.seconds_remaining)));
    setQ(0);
    setPhase("run");
  };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/assessments/${quiz.uuid}/start`);
      setAnswers({});
      begin(data);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.questions) begin(err.response.data); // resume
      else toast.error(errorMessage(err, "Couldn't start the quiz."));
    }
    setBusy(false);
  };

  const submit = async () => {
    if (!attempt || busy) return;
    setBusy(true);
    try {
      const payload = Object.entries(answers).map(([id, v]) => ({ question_id: Number(id), ...(Array.isArray(v) ? { option_ids: v } : { option_id: v }) }));
      await client.post(`/attempts/${attempt.attempt_uuid}/submit`, { answers: payload });
      const { data } = await client.get(`/attempts/${attempt.attempt_uuid}/result`);
      setResult(data);
      setPhase("result");
      onDone?.();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't submit the quiz."));
    }
    setBusy(false);
  };

  useEffect(() => {
    if (phase !== "run") return undefined;
    if (left <= 0) { toast.warn("Time's up. Submitting your answers."); submit(); return undefined; }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, left]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === "run") {
    const questions = attempt.questions || [];
    const question = questions[q];
    const multiple = question?.type === "multiple";
    const chosen = answers[question?.id];
    const toggle = (optId) => setAnswers((a) => {
      if (!multiple) return { ...a, [question.id]: optId };
      const cur = Array.isArray(a[question.id]) ? a[question.id] : [];
      return { ...a, [question.id]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
    });
    const last = q === questions.length - 1;
    return (
      <QuizGuard active mark={mark}>
      <div className="runner">
        <div className="timer">
          <span><small>{quiz.title}</small><br /><b>{clock(left)}</b> <small>left</small></span>
          <div className="qdots">
            {questions.map((x, i) => (
              <i key={x.id} className={i === q ? "now" : answers[x.id] !== undefined && (!Array.isArray(answers[x.id]) || answers[x.id].length) ? "ans" : ""}
                role="button" tabIndex={0} onClick={() => setQ(i)} onKeyDown={(e) => e.key === "Enter" && setQ(i)} style={{ cursor: "pointer" }}>{i + 1}</i>
            ))}
          </div>
        </div>
        {question && (
          <section className="question">
            <span className="course-tag">Question {q + 1} of {questions.length}{multiple ? " · choose all that apply" : ""}</span>
            <h3>{question.question_text}</h3>
            {question.options.map((o, i) => (
              <button key={o.id} className="choice" type="button" onClick={() => toggle(o.id)}
                aria-pressed={multiple ? Array.isArray(chosen) && chosen.includes(o.id) : chosen === o.id}>
                <span>{"ABCDEFGH"[i]}</span>{o.option_text}
              </button>
            ))}
          </section>
        )}
        <div className="cta-row">
          {q > 0 && <button className="cbtn quiet" type="button" onClick={() => setQ(q - 1)}>Previous</button>}
          <button className="cbtn go" type="button" onClick={last ? submit : () => setQ(q + 1)} disabled={busy}>{last ? (busy ? "Submitting…" : "Submit quiz") : "Next question"}</button>
          <small>When time runs out, your answers are submitted automatically.</small>
        </div>
      </div>
      </QuizGuard>
    );
  }

  if (phase === "result" && result) {
    const score = Math.round(result.score);
    const right = (result.answers || []).filter((x) => x.is_correct).length;
    return (
      <QuizGuard active mark={mark}>
        <div className="detail-h"><span className="kicker">{quiz.weekLabel} · Quiz</span><h2>{quiz.title}</h2></div>
        <section className="score">
          <div className="ring" style={{ background: `conic-gradient(var(--${result.passed ? "good" : "brand"}) 0 ${score}%, var(--line) 0)` }}><b>{score}%</b></div>
          <div>
            <h3>{result.passed ? "Passed" : "Not passed yet"}</h3>
            <p className="pass" style={result.passed ? undefined : { color: "var(--brand)" }}>{right} of {(result.answers || []).length} correct · pass mark {result.passing_score}%</p>
            <p style={{ color: "var(--muted)", fontSize: 13.5, marginTop: 4 }}>Your best score counts toward your grades. You can retake it after {4} hours{attemptsLeft !== null ? `, ${Math.max(0, attemptsLeft - 1)} attempts left` : ""}.</p>
          </div>
        </section>
        {(result.answers || []).length > 0 && (
          <section className="card">
            <div className="card-h"><h3>Review answers</h3></div>
            <div className="review">
              {result.answers.map((x, i) => (
                <div key={i} className="rv">
                  <i className={x.is_correct ? "y" : "n"}>{x.is_correct ? "✓" : "✕"}</i>
                  <div>{x.question}
                    <small>Your answer: {(x.selected_option ?? (x.selected_options || []).join(", ")) || "—"}{!x.is_correct ? ` · Correct: ${x.correct_option ?? (x.correct_options || []).join(", ")}` : ""}</small>
                    {x.explanation && <small>{x.explanation}</small>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </QuizGuard>
    );
  }

  return (
    <>
      <div className="detail-h"><span className="kicker">{quiz.weekLabel} · Quiz</span><h2>{quiz.title}</h2><p>{quiz.description || "A timed quiz on this week's lessons. Your best score counts toward your grade."}</p></div>
      <div className="facts">
        <div className="fact"><b>{quiz.time_limit_minutes} min</b><span>Time limit</span></div>
        <div className="fact"><b>{quiz.questions_count}</b><span>Questions</span></div>
        <div className="fact"><b>{quiz.passing_score}%</b><span>Pass mark</span></div>
        <div className="fact"><b>{attemptsLeft === null ? "Unlimited" : `${attemptsLeft} of ${quiz.max_attempts}`}</b><span>Attempts left</span></div>
      </div>
      {quiz.attempts_used > 0 && (
        <section className={`complete-box ${quiz.passed ? "ok" : ""}`}><div><strong>{quiz.passed ? "✓ Passed" : "Not passed yet"} · best score {Math.round(quiz.best_score)}%</strong><small>{quiz.attempts_used} {quiz.attempts_used === 1 ? "attempt" : "attempts"} so far.</small></div></section>
      )}
      <section className="card">
        <div className="card-h"><h3>Before you start</h3></div>
        <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}>
          <li>{quiz.opens_at ? `Opens ${fmtDayTime(quiz.opens_at)}` : "Open now"}{quiz.closes_at ? ` · closes ${fmtDayTime(quiz.closes_at)}` : ""}.</li>
          <li>The timer starts when you press Start and keeps running if you leave the page.</li>
          <li>When time runs out, your answers are submitted automatically.</li>
          <li>Copying and screenshots are blocked, and the questions hide when you leave this window.</li>
        </ul>
      </section>
      <div className="cta-row">
        <button className="cbtn go" type="button" onClick={start} disabled={!canStart || busy}>{busy ? "Starting…" : quiz.attempts_used ? "Retake quiz" : "Start quiz"}</button>
        {!canStart && <small>{quiz.window === "upcoming" ? `Opens ${fmtDayTime(quiz.opens_at)}.` : quiz.window === "closed" ? "This quiz has closed." : "No attempts left."}</small>}
      </div>
    </>
  );
}
